import { Component, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { ConferenceEligibilityService } from 'src/app/core/services/conference-eligibility.service';
import {
  ConferenceApplicationModel,
  ConferenceApplicationReviewModel,
  ConferenceApplicationStatus,
  ConferenceSelectOptionModel
} from 'src/app/models/conferenceEligibilityModels';
import { AlertService } from 'src/app/shared/services/alert.service';

interface ReviewAction {
  statusID: number;
  label: string;
}

@Component({
  selector: 'app-conference-application-review',
  templateUrl: './conference-application-review.component.html',
  standalone: false
})
export class ConferenceApplicationReviewComponent extends BaseComponent implements OnInit {
  applications: ConferenceApplicationModel[] = [];
  statuses: ConferenceSelectOptionModel[] = [];
  selectedApplication: ConferenceApplicationModel;
  selectedStatusID: number;
  notes = '';
  isOverride = false;
  loading = true;
  saving = false;
  showDetail = false;
  statusFilter: number;
  conferenceFilter = '';
  roleFilter = '';
  submittedDateFilter = '';

  constructor(
    private conferenceEligibilityService: ConferenceEligibilityService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.load();
  }

  get filteredApplications() {
    const conference = this.conferenceFilter.trim().toLowerCase();
    const role = this.roleFilter.trim().toLowerCase();
    return this.applications.filter(application =>
      (!this.statusFilter || application.statusID === Number(this.statusFilter))
      && (!conference || application.conferenceTitle.toLowerCase().includes(conference))
      && (!role || (application.eligibilitySnapshot?.roleCategoryName || '').toLowerCase().includes(role))
      && (!this.submittedDateFilter || application.submittedDate.substring(0, 10) === this.submittedDateFilter)
    );
  }

  get reviewActions(): ReviewAction[] {
    switch (this.selectedApplication?.statusID) {
      case ConferenceApplicationStatus.PendingHrReview:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.PendingOversight, label: 'Send to Oversight' },
          { statusID: ConferenceApplicationStatus.PendingCvp, label: 'Send to CVP' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' }
        ];
      case ConferenceApplicationStatus.PendingOversight:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.PendingCvp, label: 'Send to CVP' },
          { statusID: ConferenceApplicationStatus.PendingHrReview, label: 'Return to HR' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' }
        ];
      case ConferenceApplicationStatus.PendingCvp:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' },
          { statusID: ConferenceApplicationStatus.PendingHrReview, label: 'Return to HR' }
        ];
      default:
        return [];
    }
  }

  load() {
    this.loading = true;
    this.subs.push(
      this.conferenceEligibilityService.getApplications().subscribe({
        next: response => {
          this.applications = response.applications;
          this.statuses = response.statuses;
          this.loading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load conference applications.'));
          this.loading = false;
        }
      })
    );
  }

  openApplication(application: ConferenceApplicationModel) {
    this.subs.push(
      this.conferenceEligibilityService.getApplication(application.id).subscribe({
        next: detail => {
          this.selectedApplication = detail;
          this.selectedStatusID = null;
          this.notes = '';
          this.isOverride = false;
          this.showDetail = true;
        },
        error: error => this.alertService.error(this.errorMessage(error, 'Unable to load the application.'))
      })
    );
  }

  submitReview() {
    if (!this.selectedStatusID) {
      this.alertService.error('Select a review action.');
      return;
    }
    if ((this.selectedStatusID === ConferenceApplicationStatus.Approved
      || this.selectedStatusID === ConferenceApplicationStatus.Denied) && !this.notes.trim()) {
      this.alertService.error('A decision note is required for approval or denial.');
      return;
    }

    const review: ConferenceApplicationReviewModel = {
      statusID: Number(this.selectedStatusID),
      notes: this.notes.trim(),
      isOverride: this.isOverride
    };
    this.saving = true;
    this.subs.push(
      this.conferenceEligibilityService.reviewApplication(this.selectedApplication.id, review).subscribe({
        next: application => {
          const index = this.applications.findIndex(item => item.id === application.id);
          if (index >= 0) {
            this.applications[index] = application;
          }
          this.selectedApplication = application;
          this.selectedStatusID = null;
          this.notes = '';
          this.isOverride = false;
          this.saving = false;
          this.alertService.success(`Application moved to ${application.statusLabel}.`);
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to update the conference application.'));
          this.saving = false;
        }
      })
    );
  }

  fileUrl(answerId: number) {
    return this.conferenceEligibilityService.getAnswerFileUrl(answerId);
  }

  private errorMessage(error: any, fallback: string) {
    return typeof error?.error === 'string'
      ? error.error
      : error?.error?.message || error?.message || fallback;
  }
}
